import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import { useComplexMapStore } from '@/store/complexMapStore';

export const useFieldStore = defineStore('fieldStore', {
	state: () => ({
		/** Map of locationId -> field[] for caching */
		_fieldsByLocation: new Map()
	}),

	actions: {
		/**
		 * Fetch all fields for a given location.
		 *
		 * @param {number} locationId
		 * @param {Object} opts
		 * @param {boolean} opts.force  bypass cache
		 * @returns {Array} SimpleFieldDTO[]
		 */
		async fetchFieldsForLocation(locationId, { force = false } = {}) {
			if (!force && this._fieldsByLocation.has(locationId)) {
				return this._fieldsByLocation.get(locationId);
			}
			try {
				const { data } = await apiClient.get(`/fields/locations/${locationId}`);
				const fields = Array.isArray(data) ? data : [];
				this._fieldsByLocation.set(locationId, fields);
				return fields;
			} catch {
				return [];
			}
		},

		/**
		 * Create a new field at a location.
		 *
		 * @param {number} locationId
		 * @param {Object} fieldData { fieldNumber, fieldType, notes? }
		 * @returns {Object} SimpleFieldDTO
		 */
		async createField(locationId, fieldData) {
			const { data } = await apiClient.post(`/fields/locations/${locationId}/save`, fieldData);
			this._fieldsByLocation.delete(locationId);
			useComplexMapStore().invalidate(locationId);
			return data;
		},

		/**
		 * Update a field.
		 *
		 * @param {number} fieldId
		 * @param {Object} fieldData { fieldNumber, fieldType, notes? }
		 * @returns {Object} SimpleFieldDTO
		 */
		async updateField(fieldId, fieldData) {
			const { data } = await apiClient.patch(`/fields/edit/${fieldId}`, fieldData);
			if (data?.location?.locationId) {
				this._fieldsByLocation.delete(data.location.locationId);
				useComplexMapStore().invalidate(data.location.locationId);
			}
			return data;
		},

		/**
		 * Update or clear a field's map coordinates.
		 *
		 * @param {number} fieldId
		 * @param {Object} data { latitude, longitude } (both null clears pin)
		 * @returns {Object} SimpleFieldDTO
		 */
		async updateFieldCoordinates(fieldId, data) {
			const payload = {
				latitude: data.latitude ?? null,
				longitude: data.longitude ?? null
			};
			const { data: updated } = await apiClient.patch(`/fields/edit/${fieldId}/coordinates`, payload);
			if (updated?.location?.locationId) {
				this._fieldsByLocation.delete(updated.location.locationId);
				useComplexMapStore().invalidate(updated.location.locationId);
			}
			return updated;
		},

		/**
		 * Delete a field.
		 *
		 * @param {number} fieldId
		 * @param {number} locationId  to invalidate cache
		 */
		async deleteField(fieldId, locationId) {
			await apiClient.delete(`/fields/delete/${fieldId}`);
			if (locationId) {
				this._fieldsByLocation.delete(locationId);
				useComplexMapStore().invalidate(locationId);
			}
		},

		/**
		 * Get cached fields for a location (no network call).
		 */
		getCachedFields(locationId) {
			return this._fieldsByLocation.get(locationId) || [];
		}
	}
});
